/**
 * ChoirAttendanceTab — Attendance leaderboard, alerts, and event modal.
 */
import React from 'react';
import { Download, Award, AlertTriangle, X } from 'lucide-react';

export default function ChoirAttendanceTab({
  choirAlerts,
  approvedMembers,
  selectedEventAttendance,
  eventAttendanceRecords,
  onCloseEventAttendance,
  onDownloadAttendanceReport,
}) {
  return (
    <div className="space-y-4" data-testid="attendance-panel">
      <div className="bg-gradient-to-r from-violet-50 to-amber-50 rounded-2xl p-5 border-2 border-violet-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-violet-900 text-lg">Attendance Report</h3>
          <p className="text-sm text-slate-600 mt-1">Involvement & attendance across all choir events.</p>
        </div>
        <button
          onClick={onDownloadAttendanceReport}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 font-bold shadow-lg hover:from-amber-400 flex items-center gap-2"
          data-testid="download-attendance-report"
        >
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      {/* Alerts section */}
      {choirAlerts.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5" data-testid="attendance-alerts">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-bold text-red-900">Members Missing Recent Events</h3>
            <span className="ml-auto text-sm text-red-700 font-bold">{choirAlerts.length}</span>
          </div>
          <p className="text-sm text-red-700 mb-3">
            These members missed the last 2 past events. An email has been sent to them from choir@cccvob.com.
          </p>
          <div className="space-y-2">
            {choirAlerts.map(a => (
              <div key={a.member_id} className="bg-white rounded-lg p-3 flex items-center justify-between" data-testid={`alert-${a.member_id}`}>
                <div>
                  <p className="font-semibold text-slate-800">{a.member_name}</p>
                  <p className="text-xs text-slate-500">
                    Missed: {a.missed_events.map(e => `${e.title} (${e.date})`).join('; ')}
                  </p>
                </div>
                <span className="text-xs text-slate-500">{a.member_email || 'No email'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top attendees leaderboard */}
      <div className="bg-white rounded-2xl shadow border overflow-hidden">
        <div className="bg-violet-50 px-4 py-3 border-b flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" />
          <h3 className="font-bold text-violet-900">Involvement — Member Attendance</h3>
        </div>
        {approvedMembers.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No approved members to rank.</p>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider px-4 py-3">Rank</th>
                <th className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider px-4 py-3">Member</th>
                <th className="text-center text-xs font-bold text-slate-600 uppercase tracking-wider px-4 py-3">Attended</th>
                <th className="text-center text-xs font-bold text-slate-600 uppercase tracking-wider px-4 py-3">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[...approvedMembers].sort((a, b) => b.attended_count - a.attended_count).map((m, idx) => (
                <tr key={m.id}>
                  <td className="px-4 py-3 font-bold text-slate-700">
                    {idx < 3 ? ['🥇','🥈','🥉'][idx] : `#${idx + 1}`}
                  </td>
                  <td className="px-4 py-3 font-medium">{m.full_name}</td>
                  <td className="px-4 py-3 text-center">{m.attended_count}/{m.total_events}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-bold ${m.attendance_rate >= 75 ? 'text-green-600' : m.attendance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {m.attendance_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Event attendance modal */}
      {selectedEventAttendance && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onCloseEventAttendance}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-violet-800 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold">{selectedEventAttendance.title}</h3>
                <p className="text-xs text-violet-200">{selectedEventAttendance.date} at {selectedEventAttendance.start_time}</p>
              </div>
              <button onClick={onCloseEventAttendance} className="p-1.5 rounded hover:bg-violet-900">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              {eventAttendanceRecords.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No one scanned yet.</p>
              ) : (
                <ul className="divide-y">
                  {eventAttendanceRecords.map(r => (
                    <li key={r.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{r.member_name}</p>
                        <p className="text-xs text-slate-500">{r.member_email}</p>
                      </div>
                      <span className="font-mono text-sm text-violet-700 bg-violet-50 px-3 py-1 rounded-lg">{r.time_of_arrival}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
